import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  createCoreRowModel,
  createSortedRowModel,
  flexRender,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { get } from '~/lib/api'
import type { User } from '~/lib/types'
import Spinner from '~/components/Spinner'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardContent } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'

type UsersPayload = {
  users: User[]
  total: number
  page: number
  per_page: number
}

// v9 composes features explicitly, and the row models ride along with them.
// Search and paging happen in Rails; sorting is applied to the loaded page.
const features = tableFeatures({
  rowSortingFeature,
  coreRowModel: createCoreRowModel(),
  sortedRowModel: createSortedRowModel(),
})
const column = createColumnHelper<typeof features, User>()

export default function Team() {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [sorting, setSorting] = useState<SortingState>([])

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['users', query, page],
    queryFn: () => get<UsersPayload>(`/api/v1/users?page=${page}&q=${encodeURIComponent(query)}`),
    // Keeps the table on screen while the next page loads instead of flashing.
    placeholderData: keepPreviousData,
  })

  // ColumnDef is invariant in its value type and each accessor infers its own,
  // so the array is widened once here rather than at every use site.
  const columns = useMemo<ColumnDef<typeof features, User, any>[]>(
    () => [
      column.accessor('name', {
        header: 'Account',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar className="size-8">
              {row.original.avatar_url && <AvatarImage src={row.original.avatar_url} alt="" />}
              <AvatarFallback>{row.original.name[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate font-medium">{row.original.name}</p>
              <p className="truncate text-xs text-faint">{row.original.email}</p>
            </div>
          </div>
        ),
      }),
      column.accessor('provider', {
        header: 'Sign-in',
        cell: ({ getValue }) => (
          <Badge variant="outline">{getValue() === 'google_oauth2' ? 'Google' : 'Password'}</Badge>
        ),
      }),
      column.accessor('created_at', {
        header: 'Joined',
        cell: ({ getValue }) => (
          <span className="[font-variant-numeric:tabular-nums]">
            {new Date(getValue()).toLocaleDateString()}
          </span>
        ),
      }),
    ],
    [],
  )

  const table = useTable<typeof features, User>({
    features,
    data: data?.users ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
  })

  const lastPage = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
        <p className="mt-1 text-sm text-ink-2">People who can sign in to this workspace.</p>
      </div>

      <div className="flex items-center gap-3">
        <Input
          type="search"
          value={query}
          placeholder="Search name or email"
          className="max-w-xs"
          onChange={(event) => {
            setQuery(event.target.value)
            setPage(1)
          }}
        />
        {isFetching && !isPending && <Spinner label="Updating" />}
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {isPending ? (
            <div className="p-8">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const sorted = header.column.getIsSorted()
                      const Icon = sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ChevronsUpDown

                      return (
                        <TableHead key={header.id} className="px-5">
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className="inline-flex items-center gap-1.5 hover:text-ink"
                            aria-label={`Sort by ${String(header.column.columnDef.header)}`}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <Icon className="size-3" aria-hidden="true" />
                          </button>
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getAllCells().map((cell) => (
                      <TableCell key={cell.id} className="px-5 py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {table.getRowModel().rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="py-10 text-center text-faint">
                      No accounts match “{query}”.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <p className="text-faint">
          {data?.total ?? 0} account{data?.total === 1 ? '' : 's'}
        </p>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="text-faint">
            Page {page} of {lastPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
